<template>
  <v-card color="#121212" class="rounded-xl border-glow" flat>
    <div class="pa-5">
      <div class="d-flex align-center mb-4">
        <v-avatar color="red darken-3" size="40" class="mr-3 elevation-4">
          <v-icon small color="white">mdi-file-cancel</v-icon>
        </v-avatar>
        <div>
          <h3 class="text-h6 font-weight-bold white--text">Kündigungs-Assistent</h3>
          <div class="caption grey--text">Basisdatum: {{ emailDate ? new Date(emailDate).toLocaleDateString() : "Heute (Kein Maildatum)" }}</div>
        </div>
        <v-spacer></v-spacer>
        <v-btn icon small color="grey" @click="resetWizard" v-if="step > 1">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
        <v-btn icon small color="grey" @click="$emit('cancel')" class="ml-2">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-divider class="grey darken-3 mb-4"></v-divider>

      <div v-if="step === 0">
        <p class="body-2 grey--text text--lighten-1 mb-3">Welchen Vertrag möchte {{ user.fullName }} kündigen?</p>
        <div v-if="loadingContracts" class="text-center py-4">
          <v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
        </div>
        <div v-else-if="analyzedContracts.length === 0" class="text-center py-4 error--text">Keine aktiven Verträge im TAC gefunden.</div>

        <v-list v-else color="transparent" dark dense two-line>
          <v-list-item-group v-model="selectedContractIndex" color="primary">
            <v-list-item
              v-for="(item, i) in analyzedContracts"
              :key="i"
              class="mb-3 rounded-lg border-subtle"
              :class="{ 'opacity-50': item.disabled }"
              style="background: rgba(255, 255, 255, 0.03)"
              :disabled="item.disabled"
              @click="selectContract(item.contract)">
              <v-list-item-avatar>
                <v-icon :color="item.statusColor" class="elevation-2">{{ item.icon }}</v-icon>
              </v-list-item-avatar>
              <v-list-item-content>
                <div class="d-flex align-center justify-space-between mb-1">
                  <v-list-item-title class="font-weight-bold">{{ item.contract.NAME }}</v-list-item-title>
                  <v-chip x-small label :color="item.statusColor" class="font-weight-bold">{{ item.statusText }}</v-chip>
                </div>
                <v-list-item-subtitle class="caption d-flex flex-column">
                  <span class="grey--text text--lighten-1">
                    Laufzeit bis: <strong :class="item.isLate ? 'orange--text' : 'white--text'">{{ formatDate(item.contract.TERM_ENDDATE) }}</strong>
                  </span>

                  <span v-if="item.requiresManualCheck" class="red--text text--accent-2 mt-1" style="font-size: 11px; line-height: 1.2">
                    <v-icon x-small color="red accent-2" class="mr-1">mdi-alert</v-icon>
                    Mail war fristgerecht, aber Systemfrist ist heute abgelaufen. Vertrag wurde evtl. bereits verlängert.
                  </span>

                  <span v-else-if="item.isLate" class="orange--text text--lighten-2 mt-1" style="font-size: 11px">
                    Mail kam nach der Frist. Kündigung zum nächstmöglichen Termin.
                  </span>

                  <span v-if="item.disabled" class="grey--text text--darken-1 mt-1 font-italic" style="font-size: 11px">
                    Vertrag liegt in der Vergangenheit.
                  </span>
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action v-if="!item.disabled"><v-icon small color="grey">mdi-chevron-right</v-icon></v-list-item-action>
            </v-list-item>
          </v-list-item-group>
        </v-list>
      </div>

      <div v-else-if="step === 1">
        <div class="d-flex align-center mb-3">
          <v-btn color="white" icon small @click="step = 0" class="mr-2"><v-icon>mdi-arrow-left</v-icon></v-btn>
          <span class="font-weight-bold white--text text-truncate">{{ activeContract.NAME }}</span>
        </div>

        <v-alert :color="isLate ? 'orange darken-4' : 'green darken-3'" dense text class="mb-4 rounded-lg">
          <div class="d-flex align-start justify-space-between" style="width: 100%">
            <div class="d-flex align-start">
              <v-icon :color="isLate ? 'orange' : 'green'" left>{{ isLate ? "mdi-clock-alert" : "mdi-check-circle" }}</v-icon>
              <div>
                <div class="font-weight-bold text-caption">
                  {{
                    activeContract && activeContract.TERM_STARTDATE && emailDate && new Date(activeContract.TERM_STARTDATE) > new Date(emailDate)
                      ? "Bereits verlängert"
                      : isLate
                      ? "Verspätet"
                      : "Fristgerecht"
                  }}
                </div>
                <div class="caption">
                  Kündigung per: <strong>{{ formattedTerminationDate }}</strong> <br />
                  <span class="grey--text" style="font-size: 10px">Basis: Maileingang {{ formatDate(emailDate) }}</span>
                </div>
              </div>
            </div>
            <v-btn v-if="isLate" x-small text color="white" class="ml-2" style="opacity: 0.8" @click="forceOnTime">
              <v-icon left small>mdi-gavel</v-icon> Trotzdem OK
            </v-btn>
            <v-btn v-else x-small text color="white" class="ml-2" style="opacity: 0.8" @click="forceLate">
              <v-icon left small>mdi-clock-alert</v-icon> Trotzdem zu spät
            </v-btn>
          </div>
        </v-alert>

        <v-alert v-if="isSystemLate && !isLate" type="error" dense outlined class="mb-4 rounded-lg" style="background: rgba(255, 0, 0, 0.1)">
          <div class="caption font-weight-bold mb-1"><v-icon small color="red" left>mdi-alert-decagram</v-icon> Manuelle Prüfung erforderlich</div>
          <div class="caption" style="line-height: 1.3">
            Die Mail kam fristgerecht, aber die Kündigungsfrist im TAC ist <strong>heute</strong> bereits abgelaufen. Der Vertrag hat sich im System
            wahrscheinlich schon verlängert.
          </div>
          <div class="caption mt-2 yellow--text font-weight-bold">
            ➔ Bitte nur PDF/Mail senden und das Datum manuell im TAC korrigieren/zurücksetzen!
          </div>
        </v-alert>

        <div v-if="isMainContract" class="mb-4 pa-3 rounded-lg border-subtle" style="background: rgba(255, 100, 0, 0.05)">
          <div class="caption orange--text text--lighten-1 mb-1 font-weight-bold">
            <v-icon x-small color="orange lighten-1" class="mr-1">mdi-alert-circle-outline</v-icon>
            Achtung: Komplettes Abo
          </div>

          <div v-if="matchingAddons.length > 0">
            <div class="caption grey--text mb-2">
              Folgende Pakete enden ebenfalls am <strong>{{ formattedTerminationDate }}</strong> und werden mitgekündigt:
            </div>
            <div v-for="addon in matchingAddons" :key="addon.CONTRACTID" class="d-flex align-center py-0 pl-2">
              <v-icon x-small color="grey" class="mr-2">mdi-link</v-icon>
              <span class="caption white--text">{{ addon.NAME }}</span>
            </div>
          </div>
          <div v-else class="caption grey--text font-italic pl-2">Keine Zusatzpakete mit Enddatum {{ formattedTerminationDate }} gefunden.</div>
        </div>

        <div v-else-if="!isMainContract && otherAddons.length > 0" class="mb-4 pa-3 rounded-lg" style="background: rgba(255, 255, 255, 0.03)">
          <div class="caption grey--text mb-2 font-weight-bold">Weitere Zusatzpakete ebenfalls kündigen?</div>
          <div v-for="addon in otherAddons" :key="addon.CONTRACTID" class="d-flex align-center py-1">
            <v-checkbox v-model="selectedAddonIds" :value="addon.CONTRACTID" hide-details dense class="ma-0 pa-0" color="red lighten-1"></v-checkbox>
            <div class="ml-2 overflow-hidden">
              <div class="body-2 white--text text-truncate">{{ addon.NAME }}</div>
              <div class="caption grey--text" style="font-size: 10px">{{ formatDate(addon.TERM_ENDDATE) }}</div>
            </div>
          </div>
        </div>

        <p class="caption grey--text mb-2">Kündigungsgrund wählen:</p>
        <v-select
          v-model="selectedReasonId"
          :items="reasons"
          item-text="Name"
          item-value="ReasonID"
          outlined
          dark
          dense
          rounded
          background-color="#1A1A1A"
          placeholder="Grund wählen..."
          :loading="loadingDetails"></v-select>

        <div v-if="isLate && !contractToTerminate" class="caption red--text mb-3">
          Fehler: Kein Folgevertrag für die verspätete Kündigung gefunden.
        </div>

        <v-btn block depressed color="primary" class="rounded-lg" :disabled="!selectedReasonId || !contractToTerminate" @click="step = 2">
          Weiter zur Prüfung
        </v-btn>
      </div>

      <div v-else-if="step === 2">
        <div class="text-center py-2">
          <v-alert v-if="isSystemLate && !isLate" type="warning" dense text class="mb-4 text-left caption">
            <strong>Hinweis:</strong> Die API-Kündigung wird wahrscheinlich fehlschlagen oder das falsche Datum treffen, da TAC evtl. schon verlängert
            hat. Bitte prüfe dies manuell nach dem Senden.
          </v-alert>

          <p class="body-2 white--text mb-4">
            Möchtest du <strong>{{ contractsToProcessPreview.length }} Vertrag/Verträge</strong> jetzt verbindlich kündigen?
          </p>

          <div class="mb-4 text-left px-4 py-2 rounded-lg" style="background: rgba(255, 255, 255, 0.02)">
            <div class="caption grey--text mb-1">Wird gekündigt per {{ formattedTerminationDate }}:</div>
            <div v-for="(c, idx) in contractsToProcessPreview" :key="idx" class="caption white--text d-flex">
              <span class="mr-2">•</span>
              <span>{{ c.NAME }}</span>
              <span v-if="c.CONTRACTTYP !== 'Hauptvertrag' && isMainContract" class="grey--text ml-2">(Auto)</span>
            </div>
          </div>

          <div class="d-flex justify-space-between px-4">
            <v-btn text small color="grey" @click="step = 1">Zurück</v-btn>
            <v-btn color="red darken-1" depressed class="rounded-lg white--text" :loading="processing" @click="executeTermination">
              <v-icon left small>mdi-delete-forever</v-icon> Verbindlich Kündigen
            </v-btn>
          </div>
        </div>
      </div>

      <div v-else-if="step === 3">
        <v-alert type="success" dense text icon="mdi-check-circle" class="mb-4 rounded-lg"> Kündigung erfolgreich eingetragen! </v-alert>
        <p class="caption grey--text mb-1">Antwort an Kunden:</p>
        <v-card dark color="#000" class="pa-3 mb-3 rounded-lg border-subtle">
          <v-textarea
            v-model="mail.text"
            hide-details
            auto-grow
            rows="6"
            background-color="transparent"
            flat
            solo
            class="body-2 white--text"
            style="font-family: 'JetBrains Mono', monospace; line-height: 1.5"></v-textarea>
        </v-card>
        <div class="d-flex flex-wrap gap-2 mb-3">
          <v-chip dark color="grey darken-3" small label @click="previewPdf"
            ><v-icon left x-small color="red">mdi-file-pdf-box</v-icon> PDF Vorschau
            <v-progress-circular v-if="pdfLoading" indeterminate size="12" width="2" class="ml-2"></v-progress-circular
          ></v-chip>
        </div>
        <div class="d-flex justify-end">
          <v-btn text small color="grey" class="mr-2" @click="$emit('cancel')">Abbrechen</v-btn>
          <v-btn color="success" depressed class="rounded-lg gradient-btn" :loading="sendingMail" @click="sendTerminationEmail">
            <v-icon left small>mdi-send</v-icon> Bestätigung Senden
          </v-btn>
        </div>
      </div>
    </div>
  </v-card>
</template>

<script>
import tacApi from "@/api/tacApi"
import api from "@/api"
import { format, addDays, addYears, isAfter, differenceInDays } from "date-fns"
import { mapState } from "vuex"

export default {
  name: "MailTerminationWizard",
  props: {
    user: Object,
    emailDate: String, // ISO String des Maileingangs
  },
  data: () => ({
    roodyUser: null,
    step: 0,
    loadingContracts: false,
    loadingDetails: false,
    processing: false,
    pdfLoading: false,
    sendingMail: false,

    rawContracts: [],
    selectedContractIndex: null,
    activeContract: null,
    contractToTerminate: null,

    reasons: [],
    selectedReasonId: null,

    isLate: false,
    isSystemLate: false, // Wichtiges Flag für "Wir waren zu langsam"
    terminationDate: null,

    selectedAddonIds: [],
    terminatedContractsResult: [],
    mail: { to: "", subject: "Kündigungsbestätigung well come FIT", text: "" },
  }),

  computed: {
    ...mapState("studioStore", ["storeStudios"]),

    activeRawContracts() {
      if (!this.rawContracts) return []
      // Wir holen alle, filtern aber später in analyzedContracts visuell
      return this.rawContracts.sort((a, b) => {
        if (a.CONTRACTTYP === "Hauptvertrag" && b.CONTRACTTYP !== "Hauptvertrag") return -1
        if (a.CONTRACTTYP !== "Hauptvertrag" && b.CONTRACTTYP === "Hauptvertrag") return 1
        return new Date(a.TERM_ENDDATE) - new Date(b.TERM_ENDDATE)
      })
    },

    analyzedContracts() {
      const mailDate = this.emailDate ? new Date(this.emailDate) : new Date()
      const today = new Date()
      const CANCELLATION_PERIOD_DAYS = 44

      const allAnalyzed = this.activeRawContracts.map((contract) => {
        const endDate = new Date(contract.TERM_ENDDATE)
        const startDate = new Date(contract.TERM_STARTDATE)
        const deadline = addDays(endDate, -CANCELLATION_PERIOD_DAYS)

        const isClientLate = isAfter(mailDate, deadline)

        // PRÜFUNG AUF NACHFOLGER (für das Ausblenden)
        const hasSuccessor = this.activeRawContracts.some(
          (other) => other.CONTRACTTYP === contract.CONTRACTTYP && other.NAME === contract.NAME && new Date(other.TERM_ENDDATE) > endDate,
        )

        // PRÜFUNG AUF VORGÄNGER (für das Label "Verlängert")
        // Ein Vertrag ist eine Verlängerung, wenn ein anderer Vertrag desselben Typs
        // vor ihm endete oder das Mail-Datum vor dem Start dieses Vertrags liegt.
        const hasPredecessor = this.activeRawContracts.some(
          (other) =>
            other.CONTRACTTYP === contract.CONTRACTTYP &&
            other.NAME === contract.NAME &&
            new Date(other.TERM_ENDDATE) <= startDate &&
            other.CONTRACTID !== contract.CONTRACTID,
        )

        // Ein Vertrag gilt als "System-Verlängert", wenn das Mail vor seinem Beginn kam
        const isActuallyExtension = hasPredecessor || isAfter(startDate, mailDate)

        const isSystemLate = (!isClientLate && isAfter(today, deadline)) || hasSuccessor
        const isExpired = endDate < today

        let statusText = "Fristgerecht",
          statusColor = "green",
          icon = "mdi-check-circle",
          disabled = contract.cancelled

        if (isExpired) {
          disabled = true
          statusText = "Abgelaufen"
          statusColor = "grey"
          icon = "mdi-history"
        }
        // Wenn er ein Vorgänger-Abo hat ODER die Mail vor Vertragsstart kam -> Label "Verlängert"
        else if (isActuallyExtension) {
          statusText = "Verlängert"
          statusColor = "blue-grey darken-1"
          icon = "mdi-autorenew"
        } else if (isClientLate) {
          statusText = "Verspätet"
          statusColor = "orange darken-2"
          icon = "mdi-clock-alert"
        } else if (isSystemLate) {
          statusText = "Manuelle Prüfung"
          statusColor = "red"
          icon = "mdi-alert"
        }

        return {
          contract,
          // Wichtig: Wenn es eine Verlängerung ist, markieren wir es als "Late",
          // damit das Datum orange wird
          isLate: isClientLate || isActuallyExtension,
          requiresManualCheck: isSystemLate,
          hasSuccessor,
          statusText,
          statusColor,
          icon,
          disabled,
        }
      })

      // Wir filtern weiterhin die aus, die einen Nachfolger haben (die "alten" Dinger)
      return allAnalyzed.filter((e) => !e.disabled && !e.hasSuccessor)
    },

    isMainContract() {
      return this.activeContract?.CONTRACTTYP === "Hauptvertrag"
    },

    allAddons() {
      if (!this.activeRawContracts) return []
      return this.activeRawContracts.filter((c) => c.CONTRACTTYP === "Zusatzvertrag")
    },

    // Filtert Zusatzpakete basierend auf dem GEWÄHLTEN terminationDate
    matchingAddons() {
      if (!this.terminationDate || !this.allAddons.length) return []
      return this.allAddons.filter((addon) => {
        if (addon.CONTRACTSTATE?.toLowerCase().includes("künd")) return false
        const addonEnd = new Date(addon.TERM_ENDDATE)
        const targetEnd = new Date(this.terminationDate)
        // Toleranz von < 10 Tagen für Datumsgleichheit
        return Math.abs(differenceInDays(addonEnd, targetEnd)) < 10
      })
    },

    otherAddons() {
      if (!this.activeRawContracts || !this.activeContract) return []

      return this.activeRawContracts.filter(
        (c) =>
          c.CONTRACTTYP === "Zusatzvertrag" &&
          c.CONTRACTID !== this.activeContract.CONTRACTID &&
          c.TERM_ENDDATE === this.activeContract.TERM_ENDDATE &&
          !c.CONTRACTSTATE?.toLowerCase().includes("künd"),
      )
    },

    formattedTerminationDate() {
      return this.terminationDate ? format(this.terminationDate, "dd.MM.yyyy") : ""
    },

    contractsToProcessPreview() {
      if (!this.activeContract) return []
      if (this.isMainContract) {
        // Bei Hauptvertrag nur passende Addons nehmen
        return [this.contractToTerminate, ...this.matchingAddons]
      } else {
        const others = this.otherAddons.filter((c) => this.selectedAddonIds.includes(c.CONTRACTID))
        return [this.contractToTerminate, ...others]
      }
    },
  },

  methods: {
    previewPdf() {
      this.getPdfBlob().then((b) => b && window.open(URL.createObjectURL(b)))
    },
    formatDate(d) {
      return d ? new Date(d).toLocaleDateString() : ""
    },
    getContractColor(c) {
      return "blue"
    }, // dummy
    resetWizard() {
      this.step = 0
      this.activeContract = null
      this.contractToTerminate = null
      this.selectedContractIndex = null
      this.selectedAddonIds = []
      this.terminatedContractsResult = []
    },

    forceOnTime() {
      // Falls das System bereits verlängert hat (isSystemLate),
      // dürfen wir keine automatisierte Kündigung zulassen.

      if (this.activeContract.isSystemLate) {
        this.$toast.error(
          "Da die Frist im TAC bereits abgelaufen ist und der Vertrag höchstwahrscheinlich schon verlängert wurde, kann die Kündigung nicht automatisiert durchgeführt werden. Bitte setze das Datum manuell im TAC zurück.",
          {
            title: "Manuelle Korrektur nötig",
            icon: "mdi-alert",
            timeout: 0, // Wichtig, damit die Info nicht verschwindet, bevor sie gelesen wurde
            dismissable: true,
          },
        )
        // Wir brechen hier ab, damit der User nicht auf Step 2 kommt
        return
      }

      // Normaler "Trotzdem OK" Flow für Fälle, wo TAC noch nicht verlängert hat
      this.isLate = false
      this.terminationDate = new Date(this.activeContract.TERM_ENDDATE)
      this.contractToTerminate = this.activeContract
      this.mail.text = this.getRegularEmailHtml()
      this.$toast.success("Status auf 'Fristgerecht' gesetzt")
    },

    forceLate() {
      this.isLate = true
      const currentTermEnd = new Date(this.activeContract.TERM_ENDDATE)

      // Wenn wir sagen "Zu spät", bedeutet das: Nächstmöglicher Termin.
      // Wenn TAC schon verlängert hat (isSystemLate), IST das aktuelle Enddatum bereits der neue Termin.
      // Wenn TAC noch nicht verlängert hat, müssen wir den Folgevertrag suchen.

      const nextContract = this.rawContracts
        .filter((c) => c.CONTRACTTYP === this.activeContract.CONTRACTTYP && new Date(c.TERM_STARTDATE) > currentTermEnd)
        .sort((a, b) => new Date(a.TERM_STARTDATE) - new Date(b.TERM_STARTDATE))[0]

      if (nextContract) {
        // Es gibt schon einen Folgevertrag (z.B. TAC hat schon verlängert und neuen Eintrag gemacht)
        this.contractToTerminate = nextContract
        this.terminationDate = new Date(nextContract.TERM_ENDDATE)
      } else {
        // Kein expliziter Folgevertrag in der Liste.
        // Falls wir manuell "Zu Spät" klicken, nehmen wir an, der Kunde muss die aktuelle Laufzeit noch absitzen,
        // oder falls SystemLate, ist die Laufzeit eh schon verlängert.

        if (this.isSystemLate) {
          // System hat schon verlängert -> Das Enddatum des aktuellen Vertrags ist bereits das verlängerte Datum.
          this.contractToTerminate = this.activeContract
          this.terminationDate = currentTermEnd
        } else {
          this.contractToTerminate = this.activeContract
          this.terminationDate = currentTermEnd
          /*  // Kunde ist spät, aber System hat noch nicht verlängert.
          // Wir projizieren +1 Jahr (oder Laufzeit)
          this.contractToTerminate = this.activeContract
          let years = 1
          if (this.activeContract.NAME.includes("2 Jahr")) years = 2
          if (this.activeContract.NAME.includes("3 Jahr")) years = 3
          this.terminationDate = addYears(currentTermEnd, years)*/
        }
      }

      this.mail.text = this.getLateEmailHtml()
      this.$toast.warning("Status manuell auf 'Verspätet' gesetzt")
    },

    async fetchContracts() {
      this.loadingContracts = true
      try {
        let roodyUser = await api.getUserForManagerByEmail(this.user.email)
        if (roodyUser.status === "userNotFound") {
          this.$emit("roodyUserNotFound")
          return
        }
        this.roodyUser = roodyUser
        let res = await tacApi.fetchTACContractDetails(roodyUser._id)

        res = res.map((e) => ({
          mappedStudio: this.storeStudios.find((s) => s.tacPropertyId == e.RESSORTGROUPID),
          cancelled: e.CONTRACTSTATE?.toLowerCase().includes("künd"),
          ...e,
        }))

        this.rawContracts = res || []
      } catch (e) {
        console.error(e)
      } finally {
        this.loadingContracts = false
      }
    },

    async selectContract(contract) {
      this.activeContract = contract
      this.selectedAddonIds = []
      this.step = 1
      this.loadingDetails = true

      // Berechnung starten
      this.calculateTerminationDetails()

      try {
        const response = await tacApi.getTACReasons(contract.RESSORTGROUPID, "MEMBERSHIP_TERMINATION")
        if (response?.Reasons?.Reason) {
          this.reasons = response.Reasons.Reason.map((r) => ({
            ReasonID: parseInt(r.ReasonID.UniqueID[0].value, 10),
            Name: r.Reasons.TextElement.find((t) => t.language === "de")?.value || r.Reasons.TextElement[0]?.value,
          })).filter((reason, index, self) => index === self.findIndex((t) => t.Name === reason.Name))
        }
      } catch (e) {
        console.error(e)
      } finally {
        this.loadingDetails = false
      }
    },

    // In methods: calculateTerminationDetails()
    calculateTerminationDetails() {
      const CANCELLATION_PERIOD_DAYS = 44
      const refDate = this.emailDate ? new Date(this.emailDate) : new Date()

      const currentTermEnd = new Date(this.activeContract.TERM_ENDDATE)
      const currentTermStart = new Date(this.activeContract.TERM_STARTDATE)
      const deadline = addDays(currentTermEnd, -CANCELLATION_PERIOD_DAYS)

      // LOGIK-UPDATE:
      // Ein Vertrag gilt als "Verspätet/Verlängert", wenn:
      // 1. Das Maildatum nach der Deadline liegt
      // ODER
      // 2. Das Maildatum VOR dem Startdatum dieses aktuellen Vertrags liegt.
      // (Denn wenn ich heute den 2027er Vertrag kündigen will, das Maildatum aber im Zeitraum
      // des 2026er Vertrags lag, habe ich die Frist für 2026 offensichtlich verpasst.)

      const isAfterDeadline = isAfter(refDate, deadline)
      const isMailBeforeThisContractStarted = isAfter(currentTermStart, refDate)

      this.isLate = isAfterDeadline || isMailBeforeThisContractStarted

      if (this.isLate) {
        // Wenn zu spät, kündigen wir diesen (neuen) Vertrag zum Ende seiner Laufzeit
        this.forceLate()
      } else {
        this.terminationDate = currentTermEnd
        this.contractToTerminate = this.activeContract
        this.mail.text = this.getRegularEmailHtml()
      }
      this.mail.to = this.user.email
    },

    async executeTermination() {
      this.processing = true
      this.terminatedContractsResult = []
      try {
        if (this.isMainContract) {
          try {
            await this.terminateSingle(this.contractToTerminate)
          } catch (e) {
            console.warn("API Termination failed (expected if system late)", e)
            // Wenn es SystemLate war, ist ein Fehler erwartbar, wir machen weiter für PDF/Mail
            if (!this.isSystemLate) throw e
          }
          this.terminatedContractsResult = [this.contractToTerminate, ...this.matchingAddons]
        } else {
          const targets = [this.contractToTerminate, ...this.otherAddons.filter((c) => this.selectedAddonIds.includes(c.CONTRACTID))]
          for (const t of targets) await this.terminateSingle(t)
          this.terminatedContractsResult = targets
        }
        this.step = 3
      } catch (e) {
        this.$toast.error("Fehler: " + e.message)
      } finally {
        this.processing = false
      }
    },

    async terminateSingle(contract) {
      const data = {
        ResortId: contract.RESSORTGROUPID,
        ContractId: contract.CONTRACTID,
        ReasonId: this.selectedReasonId,
        ReasonType: "MEMBERSHIP_TERMINATION",
        CheckTermination: false,
      }
      if (location.host.includes("localhost")) return
      const response = await tacApi.terminateTACContract(data)
      if (response?.Result?.resultStatusFlag !== "SUCCESS") throw new Error(response?.Result?.Text?.[0]?.value || "Fehler")
    },

    async sendTerminationEmail() {
      this.sendingMail = true
      try {
        const base64 = await this.blobToBase64(await this.getPdfBlob())
        await api.sendTerminationMail({
          to: this.mail.to,
          subject: this.mail.subject,
          html: this.mail.text.replace(/\n/g, "<br>"),
          attachments: [{ filename: "Kündigungsbestätigung.pdf", content: base64.split(",")[1], encoding: "base64", contentType: "application/pdf" }],
        })
        this.$toast.success("Gesendet!")
        this.$emit("completed")
      } catch (e) {
        this.$toast.error(e.message)
      } finally {
        this.sendingMail = false
      }
    },

    async getPdfBlob() {
      this.pdfLoading = true
      try {
        const list = this.terminatedContractsResult.length ? this.terminatedContractsResult : [this.activeContract]
        return await api.generateTerminationPdf({
          user: this.roodyUser,
          contractsToDisplay: list,
          terminationDate: this.formattedTerminationDate,
          isLate: this.isLate,
        })
      } catch (e) {
        return null
      } finally {
        this.pdfLoading = false
      }
    },
    blobToBase64(blob) {
      return new Promise((r) => {
        const rd = new FileReader()
        rd.onload = () => r(rd.result)
        rd.readAsDataURL(blob)
      })
    },
    getRegularEmailHtml() {
      const names = this.contractsToProcessPreview.map((c) => c.NAME).join(", ")
      const firstName = this.user.firstName
      if (!this.isMainContract)
        return `Hallo ${firstName}\n\nIm Anhang senden wir dir die Kündigungsbestätigung des Zusatzpakets ${names}.\n\nWir wünschen dir weiterhin viel Freude im Training.\n\nSportliche Grüsse\nwell come FIT Team`
      return `Hallo ${firstName}\n\nAnbei senden wir dir die Kündigungsbestätigung für: ${names}.\n\nWir bedauern deinen Entscheid sehr. Wir sind stets daran, uns zu verbessern. Dürfen wir dich bitten, uns den Grund für Deine Kündigung kurz mitzuteilen?\n\nWir würden uns freuen, dich in Zukunft wieder bei uns begrüssen zu dürfen.\n\nSportliche Grüsse\nwell come FIT Team`
    },
    getLateEmailHtml() {
      const firstName = this.user.firstName
      if (!this.isMainContract) {
        const names = this.contractsToProcessPreview.map((c) => c.NAME).join(", ")
        return `Hallo ${firstName}\n\nVertragsanpassungen müssen 45 Tage vor Vertragsablauf schriftlich gemeldet werden. Dein Abonnement hat sich bereits verlängert. Deshalb können wir dir die Kündigung des Zusatzpakets ${names} per ${this.formattedTerminationDate} bestätigen.\n\nWir bedanken uns für die Kenntnisnahme und wünschen dir viel Freude im Training.\n\nSportliche Grüsse\nwell come FIT AG`
      }
      return `Hallo ${firstName}\n\nGemäss unseren Mitgliedschaftsbedingungen verlängert sich die Mitgliedschaft automatisch, wenn diese nicht fristgerecht und schriftlich (spätestens 45 Tage vor Vertragsablauf) gekündigt wird (siehe Punkt 2 der Mitgliedschaftsbedingungen auf dem Vertrag).\n\nDie Kündigungsfrist in deinem Fall ist bereits abgelaufen. Der Vertrag lässt sich nur per Vertragsablauf kündigen. Anbei senden wir dir die Kündigungsbestätigung per ${this.formattedTerminationDate}.\n\nBitte beachte, dass dein Mitgliedschaftsbeitrag somit auf Abonnementbeginn fällig ist.\n\nFolgende Varianten können wir dir anbieten:\n\n  •  Zahlung des Mitgliedschaftsbeitrages in Raten\n     (2 oder 12 Raten - dafür benötigen wir einen neuen Vertrag, es wird ein Ratenzuschlag erhoben)\n\n  •  Time-Stop\n     (Unterbruch des Abonnements - gebührenpflichtig)\n     die Zahlung des Mitgliedschaftsbeitrags bleibt bestehen / die Zahlungen der vereinbarten Raten bleibt bestehen\n\n  •  Übergabe der Mitgliedschaft an ein Nichtmitglied (mit unterzeichneter, schriftlicher Bestätigung)\n     (Umschreibegebühr und Aktivierungstarif total CHF 119.00)\n\nWir danken dir für deine Kenntnisnahme.\n\nSportliche Grüsse\nwell come FIT AG`
    },
  },
  mounted() {
    this.fetchContracts()
  },
  watch: {
    selectedAddonIds() {
      this.mail.text = this.isLate ? this.getLateEmailHtml() : this.getRegularEmailHtml()
    },
  },
}
</script>
